import { useState } from "react";
import { Icon } from "./Icon";
import { Tag } from "./Tag";
import { Button } from "./Button";
import { cn } from "../lib/cn";
import type { Project } from "../data/portfolio";

/**
 * One project, laid out like a store page: the artwork first — a big viewer
 * with the other shots railed beside it — then the title and the write-up.
 */
export function ProjectShowcase({ project }: { project: Project }) {
  const shots =
    project.images && project.images.length
      ? project.images
      : [{ src: project.image, alt: project.imageAlt }];
  const [slide, setSlide] = useState(0);
  // The slide whose embed is mounted, if any. Leaving a slide unmounts it, so
  // a trailer can't keep playing behind the artwork.
  const [playing, setPlaying] = useState<number | null>(null);
  const show = (next: number) => {
    setPlaying(null);
    setSlide((next + shots.length) % shots.length);
  };

  const description = Array.isArray(project.description)
    ? project.description
    : [project.description];
  const contain = project.imageFit === "contain";
  const hasFooter = Boolean(
    project.storeLinks?.length || project.links?.length || project.storeNote,
  );

  return (
    <article className="border-hairline bg-raised overflow-hidden rounded-card border shadow-card">
      <div className="p-5 sm:p-8">
        <div
          className={cn(
            // Thumbnails rail down the right on desktop, strip under the
            // viewer on narrow screens.
            "lg:flex lg:gap-3",
            // Icons and logos have no wide artwork to fill the full width,
            // so theirs is a narrower block.
            contain && "lg:mx-auto lg:max-w-4xl",
          )}
        >
          {/* 16:9 throughout, so no screenshot is ever cropped. */}
          <div
            className={cn(
              "group relative aspect-video w-full overflow-hidden rounded-xl bg-zinc-950 lg:min-w-0 lg:flex-1",
              contain && "p-6 sm:p-10",
            )}
          >
            <div className="relative size-full overflow-hidden">
              {shots.map((shot, index) => (
                <div
                  key={shot.src}
                  className={cn(
                    "ease-standard size-full transition-opacity duration-300",
                    index === 0 ? "relative" : "absolute inset-0",
                    index === slide
                      ? "opacity-100"
                      : "pointer-events-none opacity-0",
                  )}
                >
                  {shot.youtubeId && playing === index ? (
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${shot.youtubeId}?autoplay=1&rel=0`}
                      title={shot.alt || project.imageAlt}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="size-full border-0 bg-zinc-950"
                    />
                  ) : (
                    <>
                      <img
                        src={shot.src}
                        alt={shot.alt || project.imageAlt}
                        loading="lazy"
                        className={cn(
                          "size-full",
                          contain ? "object-contain" : "object-cover",
                        )}
                      />
                      {shot.youtubeId ? (
                        <PlayButton onClick={() => setPlaying(index)} />
                      ) : null}
                    </>
                  )}
                </div>
              ))}
            </div>

            {shots.length > 1 ? (
              <>
                <CarouselArrow
                  direction="prev"
                  onClick={() => show(slide - 1)}
                />
                <CarouselArrow
                  direction="next"
                  onClick={() => show(slide + 1)}
                />
              </>
            ) : null}
          </div>

          {shots.length > 1 ? (
            // The rail is absolute inside this cell on desktop, so a long strip
            // of thumbnails scrolls instead of stretching the viewer off 16:9.
            <div className="relative mt-3 lg:mt-0 lg:w-36 lg:shrink-0">
              <div className="thumb-rail flex gap-2.5 overflow-x-auto pb-1 lg:absolute lg:inset-0 lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:pr-1 lg:pb-0">
                {shots.map((shot, index) => (
                  <button
                    key={shot.src}
                    type="button"
                    aria-label={
                      shot.youtubeId
                        ? "Show trailer"
                        : `Show image ${index + 1}`
                    }
                    aria-current={index === slide}
                    onClick={() => show(index)}
                    className={cn(
                      "ease-standard relative aspect-video w-24 shrink-0 cursor-pointer overflow-hidden rounded-lg border bg-zinc-950 transition duration-200 sm:w-32 lg:w-full",
                      index === slide
                        ? "border-accent opacity-100"
                        : "border-hairline opacity-55 hover:opacity-100",
                    )}
                  >
                    <img
                      src={shot.src}
                      alt=""
                      loading="lazy"
                      className={cn(
                        "size-full",
                        contain ? "object-contain p-1.5" : "object-cover",
                      )}
                    />
                    {shot.youtubeId ? (
                      <span className="absolute inset-0 grid place-items-center bg-zinc-950/40 text-white">
                        <Icon
                          name="play"
                          size={18}
                          fill="currentColor"
                          stroke="none"
                        />
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <p className="text-accent text-xs font-semibold tracking-eyebrow-sm uppercase">
            {project.category}
          </p>
          {project.status ? (
            <span className="border-hairline bg-subtle text-body inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs leading-snug font-semibold">
              <span
                aria-hidden="true"
                className={cn(
                  "size-1.5 rounded-full",
                  project.status === "In Progress"
                    ? "bg-amber-400"
                    : "bg-emerald-400",
                )}
              />
              {project.status}
            </span>
          ) : null}
        </div>
        <h3 className="text-strong mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
          {project.title}
        </h3>
        {project.role ? (
          <p className="text-muted mt-2 text-sm font-medium">{project.role}</p>
        ) : null}

        <div className="mt-7 grid gap-8 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] lg:gap-14">
          <div>
            <div className="grid gap-3">
              {description.map((line) => (
                <p key={line} className="text-body text-base leading-7">
                  {line}
                </p>
              ))}
            </div>

            {project.highlights.length ? (
              <ul className="mt-6 grid gap-3">
                {project.highlights.map((highlight) => (
                  <li
                    key={highlight}
                    className="text-muted flex gap-3 text-sm leading-6"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-2 size-1.5 shrink-0 rounded-full bg-blue-500"
                    />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <aside className="lg:border-hairline lg:border-l lg:pl-14">
            {project.tags.length ? (
              <>
                <h4 className="text-dim text-xs font-semibold tracking-eyebrow-sm uppercase">
                  Built with
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </div>
              </>
            ) : null}

            {hasFooter ? (
              <div
                className={cn(
                  "flex flex-wrap items-center gap-3",
                  project.tags.length > 0 && "mt-8",
                )}
              >
                {(project.storeLinks ?? []).map((store) => (
                  <Button
                    key={store.platform}
                    variant="secondary"
                    size="sm"
                    href={store.href}
                    disabled={store.available === false}
                    icon={store.available === false ? undefined : "external"}
                  >
                    {store.platform === "ios"
                      ? "iOS · App Store"
                      : "Android · Google Play"}
                  </Button>
                ))}
                {project.storeNote ? (
                  <span className="text-dim text-xs">{project.storeNote}</span>
                ) : null}
                {(project.links ?? []).map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-strong inline-flex items-center gap-2 text-sm font-semibold"
                  >
                    {link.label}
                    <Icon name={link.icon ?? "external"} size={16} />
                  </a>
                ))}
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </article>
  );
}

/** Overlays a video slide's poster until the viewer asks for the embed. */
function PlayButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Play trailer"
      onClick={onClick}
      className="ease-standard absolute inset-0 grid cursor-pointer place-items-center transition duration-200 hover:bg-zinc-950/20"
    >
      <span className="border-hairline ease-standard grid size-16 place-items-center rounded-full border bg-zinc-950/65 pl-1 text-white backdrop-blur-md transition duration-200 group-hover:bg-zinc-950/80">
        <Icon name="play" size={28} fill="currentColor" stroke="none" />
      </span>
    </button>
  );
}

function CarouselArrow({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick: () => void;
}) {
  const next = direction === "next";
  return (
    <button
      type="button"
      aria-label={next ? "Next image" : "Previous image"}
      onClick={onClick}
      className={cn(
        "border-hairline hover:border-hairline-strong ease-standard absolute top-1/2 grid size-11 -translate-y-1/2 cursor-pointer place-items-center rounded-full border bg-zinc-950/60 text-white backdrop-blur-md transition duration-200 hover:bg-zinc-950/85",
        // Hover-revealed on pointer devices, always available on touch.
        "lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100",
        next ? "right-4" : "left-4",
      )}
    >
      <span className={cn("grid place-items-center", !next && "rotate-180")}>
        <Icon name="arrow" size={18} />
      </span>
    </button>
  );
}
